import { FieldsStructure, ValidationErrorsStructure, RuleStructure } from '@/contracts/forms/FieldContracts'

// Check for errors:
export function hasErrors(validationErrors: ValidationErrorsStructure, name?: string): boolean
{
    // If name exists, check if specific input has errors:
    if(name)
    {
        if(validationErrors[name] !== undefined)
        {
            return true
        }
        return false
    }
    // Else check if any errors exist:
    else if(Object.keys(validationErrors).length > 0)
    {
        return true
    }
    return false
}

// Validate input against rules:
export function validateInput(validationErrors: ValidationErrorsStructure, name: string, rule: string, config: RuleStructure, value: any, inputs: FieldsStructure): ValidationErrorsStructure
{
    switch(rule)
    {
        case 'email':
            validationErrors = validateEmail(validationErrors, name, config, value)
            break
        case 'required':
            validationErrors = validateRequired(validationErrors, name, config, value, inputs)
            break
        case 'max':
            validationErrors = validateMax(validationErrors, name, config, value, inputs)
            break
        case 'min':
            validationErrors = validateMin(validationErrors, name, config, value, inputs)
            break
        case 'confirm':
            validationErrors = validateConfirm(validationErrors, name, config, value, inputs)
            break
        case 'number':
            validationErrors = validateNumber(validationErrors, name, config, value, inputs)
            break
        case 'year':
            validationErrors = validateYear(validationErrors, name, config, value)
            break
        case 'month':
            validationErrors = validateMonth(validationErrors, name, config, value)
            break
        case 'day':
            validationErrors = validateDay(validationErrors, name, config, value)
            break
        case 'date':
            validationErrors = validateDate(validationErrors, name, config, value)
            break
        case 'password':
            validationErrors = validatePassword(validationErrors, name, config, value)
            break
        case 'requires':
            validationErrors = validateRequires(validationErrors, name, config, value, inputs)
            break
    }
    return validationErrors
}

// Validate email:
export function validateEmail(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: string): ValidationErrorsStructure
{
    // If config isn't true, return success:
    if(config.config !== true || value === undefined)
    {
        return validationErrors
    }
    // Email regex:
    const regex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/
    // Add error on fail:
    if(!regex.test(value))
    {
        const errorMessage = (config.message !== undefined ? config.message : value + ' is not a valid email')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate required:
export function validateRequired(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: string, inputs: FieldsStructure): ValidationErrorsStructure
{
    // If config isn't true, return success:
    if(config.config !== true)
    {
        return validationErrors
    }
    // Add error on fail:
    if(value === undefined || value === '')
    {
        const errorMessage = (config.message !== undefined ? config.message : inputs[name].label + ' is required')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate max characters/size:
export function validateMax(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: string, inputs: FieldsStructure): ValidationErrorsStructure
{
    // Check for date:
    if(value && inputs[name].type === 'Date')
    {
        const dateArray = value.split('/')
        const dateRule = config.config.split('/')
        let setError = false
        if(dateArray[2] > dateRule[2])
        {
            setError = true
        }
        else if(dateArray[2] === dateRule[2] && dateArray[1] > dateRule[1])
        {
            setError = true
        }
        else if(dateArray[2] === dateRule[2] && dateArray[1] === dateRule[1] && dateArray[0] > dateRule[0])
        {
            setError = true
        }
        if(setError)
        {
            const errorMessage = (config.message !== undefined ? config.message : inputs[name].label + ' must be less than ' + config.config + ' characters')
            validationErrors = addValidationError(validationErrors, name, errorMessage)
        }
    }
    // Add error on fail:
    else if(value && value.length > config.config)
    {
        const errorMessage = (config.message !== undefined ? config.message : inputs[name].label + ' must be less than ' + config.config + ' characters')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate min characters/size:
export function validateMin(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: string, inputs: FieldsStructure): ValidationErrorsStructure
{
    // Add error on fail:
    if(value && value.length < config.config)
    {
        const errorMessage = (config.message !== undefined ? config.message : inputs[name].label + ' must be more than ' + config.config + ' characters')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate confirm matches another field:
export function validateConfirm(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: string, inputs: FieldsStructure): ValidationErrorsStructure
{
    // Convert to lowercase:
    const v1 = (value ? value.toLowerCase() : '')
    const v2 = (inputs[config.config].value ? inputs[config.config].value.toLowerCase() : '')
    // Compare inputs:
    if(v1 !== v2)
    {
        const errorMessage = (config.message !== undefined ? config.message : inputs[name].label + ' must match ' + inputs[config.config].label.toLowerCase())
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate number:
export function validateNumber(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: number, inputs: FieldsStructure): ValidationErrorsStructure
{
    // If config isn't true, return success:
    if(config.config !== true || !value)
    {
        return validationErrors
    }
    // Add error on fail:
    if(!Number.isInteger(value))
    {
        const errorMessage = (config.message !== undefined ? config.message : inputs[name].label + ' must be a number')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate year:
export function validateYear(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: number|string): ValidationErrorsStructure
{
    // If config isn't true, return success:
    if(config.config !== true || !value)
    {
        return validationErrors
    }
    // Year regex:
    const regex = /^[0-9]+$/
    // Add error on fail:
    if(!regex.test(value.toString()) || value.toString().length !== 4)
    {
        const errorMessage = (config.message !== undefined ? config.message : value + ' is not a valid year')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate month:
export function validateMonth(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: number|string): ValidationErrorsStructure
{
    // If config isn't true, return success:
    if(config.config !== true || !value)
    {
        return validationErrors
    }
    // Year regex:
    const regex = /^[0-9]+$/
    // Add error on fail:
    if(!regex.test(value.toString()) || value.toString().length !== 2 || (Number(value) < 1 || Number(value) > 12))
    {
        const errorMessage = (config.message !== undefined ? config.message : value + ' is not a valid month')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate day:
export function validateDay(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: number|string): ValidationErrorsStructure
{
    // If config isn't true, return success:
    if(config.config !== true || !value)
    {
        return validationErrors
    }
    // Year regex:
    const regex = /^[0-9]+$/
    // Add error on fail:
    if(!regex.test(value.toString()) || value.toString().length !== 2 || (Number(value) < 1 || Number(value) > 31))
    {
        const errorMessage = (config.message !== undefined ? config.message : value + ' is not a valid day')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate date:
export function validateDate(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: string): ValidationErrorsStructure
{
    // If config isn't true, return success:
    if(config.config !== true || !value)
    {
        return validationErrors
    }
    const dateSplit = value.split('/')
    validationErrors = validateDay(validationErrors, name, config, dateSplit[0])
    validationErrors = validateMonth(validationErrors, name, config, dateSplit[1])
    validationErrors = validateYear(validationErrors, name, config, dateSplit[2])
    return validationErrors
}

// Validate password:
export function validatePassword(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: string): ValidationErrorsStructure
{
    // If config isn't true, return success:
    if(config.config !== true || value === undefined)
    {
        return validationErrors
    }
    // Password regex:
    const regex = /^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[#?!@$%^&*-])(?=.*?[0-9]).{8,100}$/
    // Add error on fail:
    if(!regex.test(value))
    {
        const errorMessage = (config.message !== undefined ? config.message : value + ' is not a valid password. Password must contain at least one lowercase letter, one uppercase letter, one special character and at least one digit. The password must also be between 8 and 100 characters long.')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Validate another field exists:
export function validateRequires(validationErrors: ValidationErrorsStructure, name: string, config: RuleStructure, value: string, inputs: FieldsStructure): ValidationErrorsStructure
{
    // Compare inputs:
    if(inputs[name].value && !inputs[config.config].value)
    {
        const errorMessage = (config.message !== undefined ? config.message : inputs[name].label + ' requires ' + inputs[config.config].label + ' to have a value.')
        validationErrors = addValidationError(validationErrors, name, errorMessage)
    }
    return validationErrors
}

// Add validation error to validation object:
function addValidationError(validationErrors: ValidationErrorsStructure, name: string, error: string): ValidationErrorsStructure
{
    // Add key if undefined:
    if(validationErrors[name] === undefined)
    {
        validationErrors[name] = []
    }
    // Add error:
    validationErrors[name].push({ error: error })
    return validationErrors
}
